import { OiReport, fromPoiDataFormat, toPoiDataFormat } from "../components/OI/OiReport";
import { email } from "../App";
import { EventReport } from "../components/OI/redux/eventReport";

const shouldDebugOi = (): boolean => {
    return process.env.NODE_ENV !== "production";
};

const debugOi = (label: string, data: unknown) => {
    if (!shouldDebugOi()) return;
    // Keep logs compact and safe-ish; avoid dumping whole report bodies.
    console.debug(`[OI DEBUG] ${label}`, data);
};

const summarizeReferencedDocuments = (poiData: Record<string, any>) => {
    const camel = poiData.referencedDocuments;
    const pascal = poiData.ReferencedDocuments;

    const normalize = (v: unknown): string[] => {
        if (!Array.isArray(v)) return [];
        return v.filter((x): x is string => typeof x === "string");
    };

    const camelArr = normalize(camel);
    const pascalArr = normalize(pascal);

    return {
        referencedDocuments: { count: camelArr.length, values: camelArr },
        ReferencedDocuments: { count: pascalArr.length, values: pascalArr }
    };
};

export const GetEnumerationSet = async (): Promise<Record<number, string> | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const response = await fetch(`${apiUrl}/teams/poi/testsets`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if (response.ok) {
            const result: Record<number, string> = await response.json();
            return result;
        } else {
            console.error("Failed to fetch enumeration set", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        console.error("Error fetching enumeration set:", error);
        return null;
    }
};

export const GetTags = async (name: string): Promise<string[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/teams/poi/tags?itemname=${encodeURIComponent(name)}`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if (response.ok) {
            const result: string[] = await response.json();
            // Handle comma-separated values by splitting them and flattening
            const allTags = result.flatMap(tag =>
                tag.split(',').map(t => t.trim()).filter(t => t !== '')
            );
            // Remove duplicates and empty strings
            const uniqueTags = Array.from(new Set(allTags)).filter(tag => tag && tag.trim() !== '');
            return uniqueTags;
        } else {
            console.error("Failed to fetch tags", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        console.error("Error fetching tags:", error);
        return null;
    }
};

export const GetStaff = async (): Promise<string[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const url = `${apiUrl}/api/v1/staff`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            }
        });

        if (response.ok) {
            const result: string[] = await response.json();
            return result;
        } else {
            console.error("Failed to fetch staff", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        console.error("Error fetching staff:", error);
        return null;
    }
};

export const GetPOIList = async (page: number, pageSize: number = 20, signal?: AbortSignal): Promise<OiReport[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/oi/page/${page}?pageSize=${pageSize}`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            signal
        });

        if (response.ok) {
            const result = await response.json();
            if (!Array.isArray(result)) {
                console.error("Expected array from POI list API, got:", typeof result);
                return null;
            }

            const poiReports: OiReport[] = result.map((item: Record<string, any>, index: number) => {
                return fromPoiDataFormat(item);
            });
            return poiReports;
        } else {
            console.error("Failed to fetch POI list", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error fetching POI list:", error);
        return null;
    }
};

export const FilterPOIBy = async (filter: string, page: number, pageSize: number = 20, signal?: AbortSignal): Promise<OiReport[] | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const url = `${apiUrl}/oi/filtered/page/${page}?filter=${encodeURIComponent(filter)}&pageSize=${pageSize}`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            signal
        });

        if (response.ok) {
            const result = await response.json();
            if (!Array.isArray(result)) {
                console.error("Expected array from filtered POI API, got:", typeof result);
                return null;
            }

            const poiReports: OiReport[] = result.map((item: Record<string, any>, index: number) => {
                return fromPoiDataFormat(item);
            });
            return poiReports;
        } else {
            console.error("Failed to filter POI list", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error filtering POI list:", error);
        return null;
    }
};

export const DeletePOIReport = async (guid: string, signal?: AbortSignal): Promise<boolean> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/oi/${guid}`;
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                "Content-Type": "application/json",
            },
            signal
        });

        if (response.ok) {
            return true;
        } else {
            console.error("Failed to delete POI report", response.status, response.statusText);
            return false;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return false;
        }
        console.error("Error deleting POI report:", error);
        return false;
    }
};

export const PostReport = async (report: EventReport, signal?: AbortSignal): Promise<string | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/oi/`;
        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(report),
            signal
        });

        if (response.ok) {
            return await response.text();
        } else {
            console.error("Failed to create POI report", response.status, response.statusText);
            const errorText = await response.text();
            console.error("Error response:", errorText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error creating POI report:", error);
        return null;
    }
};

export const PatchReport = async (report: EventReport, signal?: AbortSignal): Promise<OiReport | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    try {
        const url = `${apiUrl}/oi/${report.guid}`;
        const poiData = toPoiDataFormat(report);

        debugOi("PATCH /oi payload", {
            guid: report.guid,
            isDraft: report.isDraft,
            ...summarizeReferencedDocuments(poiData)
        });

        const response = await fetch(url, {
            method: "PATCH",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(poiData),
            signal
        });

        if (response.ok) {
            const text = await response.text();
            if (text) {
                try {
                    const result: Record<string, any> = JSON.parse(text);
                    const savedReport = fromPoiDataFormat(result);
                    return savedReport;
                } catch (parseError) {
                    console.error("Failed to parse response as JSON:", parseError);
                    return null;
                }
            } else {
                return report;
            }
        } else {
            const errorText = await response.text();
            console.error("Error response:", errorText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error updating POI report:", error);
        return null;
    }
};

export const GetPOIReportByGuid = async (guid: string, signal?: AbortSignal): Promise<OiReport | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/oi/${guid}`;
        console.log("Fetching POI report from URL:", url);
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            signal
        });

        if (response.ok) {
            const result: Record<string, any> = await response.json();
            const poiReport = fromPoiDataFormat(result);
            return poiReport;
        } else {
            console.error("Failed to fetch POI report", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error fetching POI report:", error);
        return null;
    }
};

export const GetPOICount = async (signal?: AbortSignal): Promise<number | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/oi/count`;

        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            signal
        });

        if (response.ok) {
            const result = await response.json();
            const count = typeof result === 'number' ? result : result.count || 0;
            return count;
        } else {
            console.error("Failed to fetch POI count", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error fetching POI count:", error);
        return null;
    }
};

export const GetFilteredPOICount = async (filter: string, signal?: AbortSignal): Promise<number | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;

    try {
        const url = `${apiUrl}/oi/filtered/count?filter=${encodeURIComponent(filter)}`;
        const response = await fetch(url, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
            signal
        });

        if (response.ok) {
            const result = await response.json();
            const count = typeof result === 'number' ? result : result.count || 0;
            return count;
        } else {
            console.error("Failed to fetch filtered POI count", response.status, response.statusText);
            return null;
        }
    } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
            return null;
        }
        console.error("Error fetching filtered POI count:", error);
        return null;
    }
};

// Helper function to get MIME type based on file extension
const getMimeType = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();

    const mimeTypes: Record<string, string> = {
        'pdf': 'application/pdf',
        'doc': 'application/msword',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'xls': 'application/vnd.ms-excel',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'ppt': 'application/vnd.ms-powerpoint',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'png': 'image/png',
        'gif': 'image/gif',
        'txt': 'text/plain',
        'csv': 'text/csv',
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        '7z': 'application/x-7z-compressed'
    };

    return mimeTypes[extension || ''] || 'application/octet-stream';
};

export const UploadFile = async (file: File): Promise<string | null> => {
    const apiUrl = process.env.REACT_APP_API_URL;
    const userEmail = email;

    try {
        const fileName = file.name;
        const mimeType = getMimeType(fileName);

        const arrayBuffer = await file.arrayBuffer();
        const fileData = new Uint8Array(arrayBuffer);

        const url = `${apiUrl}/files/upload?fileName=${encodeURIComponent(fileName)}&mail=${encodeURIComponent(userEmail)}`;

        const response = await fetch(url, {
            method: "POST",
            headers: {
                "Content-Type": mimeType,
            },
            body: fileData
        });

        if (response.ok) {
            const result = await response.text();
            return result;
        } else {
            console.error("Failed to upload file", response.status, response.statusText);
            console.error("Upload response body", await response.text());
            return null;
        }
    } catch (error) {
        console.error("Error uploading file:", error);
        return null;
    }
};